import { useState, useMemo, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { safeFormat } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  startOfDay, endOfDay, addDays, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, parseISO, isValid, format
} from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Calendar, Clock, User, Loader2, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, Search, X, Phone, History, DollarSign, ShieldAlert, Scissors,
  Upload, FileText, AlertCircle, CheckCircle2
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AuthPinModal } from "@/components/AuthPinModal";

// ─── Helpers de planilha ─────────────────────────────────────────────────────

async function downloadAppointmentTemplate() {
  const XLSX = await import("xlsx");
  const example = [
    { "Nome do Cliente (*)": "Maria Silva", "Telefone": "11999990001", "Email": "", "Serviço (*)": "Corte Feminino", "Profissional (*)": "Ana", "Data (AAAA-MM-DD) (*)": "2024-01-15", "Hora (HH:MM) (*)": "10:00", "Duração (min)": 60, "Valor (R$)": "80,00", "Status": "completed", "Observações": "" },
    { "Nome do Cliente (*)": "João Santos", "Telefone": "11999990002", "Email": "", "Serviço (*)": "Manicure", "Profissional (*)": "Carla", "Data (AAAA-MM-DD) (*)": "2024-01-16", "Hora (HH:MM) (*)": "14:30", "Duração (min)": 45, "Valor (R$)": "35,00", "Status": "completed", "Observações": "" },
    { "Nome do Cliente (*)": "Fernanda Lima", "Telefone": "11999990003", "Email": "", "Serviço (*)": "Bronzeamento", "Profissional (*)": "Ana", "Data (AAAA-MM-DD) (*)": "2024-01-17", "Hora (HH:MM) (*)": "09:00", "Duração (min)": 30, "Valor (R$)": "60,00", "Status": "completed", "Observações": "Cliente VIP" },
  ];
  const headers = ["Nome do Cliente (*)", "Telefone", "Email", "Serviço (*)", "Profissional (*)", "Data (AAAA-MM-DD) (*)", "Hora (HH:MM) (*)", "Duração (min)", "Valor (R$)", "Status", "Observações"];
  const ws = XLSX.utils.json_to_sheet(example, { header: headers });
  ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length + 2, 16) }));

  const instructions = [
    { "Campo": "Nome do Cliente (*)", "Obrigatório": "Sim", "Descrição": "Nome completo do cliente", "Exemplo": "Maria Silva" },
    { "Campo": "Telefone", "Obrigatório": "Não", "Descrição": "Telefone com DDD, somente números", "Exemplo": "11999990001" },
    { "Campo": "Email", "Obrigatório": "Não", "Descrição": "E-mail do cliente", "Exemplo": "maria@email.com" },
    { "Campo": "Serviço (*)", "Obrigatório": "Sim", "Descrição": "Nome exato do serviço cadastrado no sistema", "Exemplo": "Corte Feminino" },
    { "Campo": "Profissional (*)", "Obrigatório": "Sim", "Descrição": "Nome exato do profissional cadastrado no sistema", "Exemplo": "Ana" },
    { "Campo": "Data (AAAA-MM-DD) (*)", "Obrigatório": "Sim", "Descrição": "Data no formato AAAA-MM-DD", "Exemplo": "2024-01-15" },
    { "Campo": "Hora (HH:MM) (*)", "Obrigatório": "Sim", "Descrição": "Hora no formato HH:MM (24h)", "Exemplo": "10:00" },
    { "Campo": "Duração (min)", "Obrigatório": "Não", "Descrição": "Duração em minutos. Se vazio, usa a duração padrão do serviço.", "Exemplo": "60" },
    { "Campo": "Valor (R$)", "Obrigatório": "Não", "Descrição": "Valor cobrado. Se vazio, usa o preço padrão do serviço.", "Exemplo": "80,00" },
    { "Campo": "Status", "Obrigatório": "Não", "Descrição": "completed | confirmed | pending | cancelled | no_show. Padrão: completed", "Exemplo": "completed" },
    { "Campo": "Observações", "Obrigatório": "Não", "Descrição": "Notas internas sobre o agendamento", "Exemplo": "Cliente VIP" },
    { "Campo": "", "Obrigatório": "", "Descrição": "", "Exemplo": "" },
    { "Campo": "IMPORTANTE", "Obrigatório": "", "Descrição": "Os nomes de Serviço e Profissional devem ser EXATAMENTE iguais aos cadastrados no sistema (maiúsculas/minúsculas importam).", "Exemplo": "" },
    { "Campo": "LIMITE", "Obrigatório": "", "Descrição": "Máximo de 5.000 agendamentos por importação.", "Exemplo": "" },
  ];
  const wsInstr = XLSX.utils.json_to_sheet(instructions, { header: ["Campo", "Obrigatório", "Descrição", "Exemplo"] });
  wsInstr["!cols"] = [{ wch: 28 }, { wch: 12 }, { wch: 70 }, { wch: 20 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Modelo");
  XLSX.utils.book_append_sheet(wb, wsInstr, "Instruções");
  XLSX.writeFile(wb, "modelo_importacao_agendamentos.xlsx");
}

type ParsedAppt = {
  clientName: string;
  clientPhone?: string | null;
  clientEmail?: string | null;
  serviceName: string;
  professionalName: string;
  date: string;
  time: string;
  durationMinutes?: number | null;
  notes?: string | null;
  status?: "pending" | "confirmed" | "completed" | "cancelled" | "no_show" | null;
  totalPrice?: string | null;
};

// Converte qualquer representação de data para YYYY-MM-DD
function parseDateToISO(raw: string): string {
  const s = raw.trim();
  // Já está no formato correto YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // DD/MM/YYYY ou DD/MM/YY
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(s)) {
    const parts = s.split("/");
    const d = parts[0]!.padStart(2, "0");
    const m = parts[1]!.padStart(2, "0");
    let y = parts[2]!;
    if (y.length === 2) y = `20${y}`;
    return `${y}-${m}-${d}`;
  }
  // M/D/YYYY (formato americano do Excel com cellDates:true)
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
    const parts = s.split("/");
    // Detectar se é MM/DD/YYYY (americano) ou DD/MM/YYYY (brasileiro)
    // Heurística: se o primeiro número > 12, é dia; caso contrário assume americano
    const first = parseInt(parts[0]!, 10);
    const second = parseInt(parts[1]!, 10);
    const y = parts[2]!;
    if (first > 12) {
      // DD/MM/YYYY
      return `${y}-${String(second).padStart(2, "0")}-${String(first).padStart(2, "0")}`;
    } else {
      // MM/DD/YYYY (Excel americano)
      return `${y}-${String(first).padStart(2, "0")}-${String(second).padStart(2, "0")}`;
    }
  }
  // DD-MM-YYYY
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
    const [d, m, y] = s.split("-");
    return `${y}-${m}-${d}`;
  }
  // Serial numérico do Excel (número de dias desde 1900-01-01)
  if (/^\d+(\.\d+)?$/.test(s)) {
    const serial = Math.floor(parseFloat(s));
    // Excel epoch: 1 = 1900-01-01, mas tem bug do ano bissexto 1900
    const excelEpoch = new Date(1899, 11, 30);
    const d = new Date(excelEpoch.getTime() + serial * 86400000);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  }
  return s;
}

// Converte qualquer representação de hora para HH:MM
function parseTimeToHHMM(raw: string): string {
  const s = raw.trim();
  // Já está no formato HH:MM
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(s)) return s.slice(0, 5);
  // H:MM ou HH:MM sem segundos
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [h, m] = s.split(":");
    return `${h!.padStart(2, "0")}:${m}`;
  }
  // HHMM (4 dígitos)
  if (/^\d{4}$/.test(s)) return `${s.slice(0, 2)}:${s.slice(2)}`;
  // Decimal do Excel: 0.5 = 12:00, 0.75 = 18:00
  if (/^0\.\d+$/.test(s) || s === "0") {
    const totalMinutes = Math.round(parseFloat(s) * 24 * 60);
    const h = Math.floor(totalMinutes / 60) % 24;
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  // Formato com AM/PM: "9:00 AM", "2:30 PM"
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1]!, 10);
    const m = ampm[2]!;
    const period = ampm[3]!.toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${m}`;
  }
  return s;
}

function normalizeApptRow(row: Record<string, string>): ParsedAppt | null {
  const clientName = row["nome do cliente (*)"] || row["nome do cliente"] || row["nome"] || row["cliente"] || row["name"] || "";
  const serviceName = row["serviço (*)"] || row["servico (*)"] || row["serviço"] || row["servico"] || row["service"] || "";
  const professionalName = row["profissional (*)"] || row["profissional"] || row["professional"] || "";
  const dateRaw = row["data (aaaa-mm-dd) (*)"] || row["data"] || row["date"] || "";
  const timeRaw = row["hora (hh:mm) (*)"] || row["hora"] || row["time"] || "";
  if (!clientName.trim() || !serviceName.trim() || !professionalName.trim() || !dateRaw.trim() || !timeRaw.trim()) return null;
  const normalizedDate = parseDateToISO(dateRaw);
  const normalizedTime = parseTimeToHHMM(timeRaw);
  // Validar resultado final
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) return null;
  if (!/^\d{2}:\d{2}$/.test(normalizedTime)) return null;
  const durationRaw = row["duração (min)"] || row["duracao (min)"] || row["duração"] || row["duration"] || "";
  const priceRaw = row["valor (r$)"] || row["valor"] || row["preço"] || row["preco"] || row["price"] || "";
  const statusRaw = (row["status"] || "").toLowerCase().trim();
  const validStatuses = ["pending", "confirmed", "completed", "cancelled", "no_show"];
  return {
    clientName: clientName.trim(),
    clientPhone: row["telefone"] || row["phone"] || null,
    clientEmail: row["email"] || null,
    serviceName: serviceName.trim(),
    professionalName: professionalName.trim(),
    date: normalizedDate,
    time: normalizedTime,
    durationMinutes: durationRaw ? parseInt(durationRaw, 10) || null : null,
    totalPrice: priceRaw ? priceRaw.replace(",", ".") : null,
    status: validStatuses.includes(statusRaw) ? (statusRaw as ParsedAppt["status"]) : "completed",
    notes: row["observações"] || row["observacoes"] || row["notes"] || null,
  };
}

const APPT_CHUNK_SIZE = 200;

// ─── Import Modal ─────────────────────────────────────────────────────────────

function ImportAppointmentsModal({ open, onClose, onSuccess }: { open: boolean; onClose: () => void; onSuccess: () => void }) {
  const [parsed, setParsed] = useState<ParsedAppt[]>([]);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();

  const importMutation = (trpc as any).appointments.bulkImport.useMutation({
    onError: (e: any) => toast.error(e.message),
  });

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    setParsed([]);
    const file = e.target.files?.[0];
    if (!file) return;
    const isXLSX = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
    const isCSV = file.name.endsWith(".csv");
    if (!isXLSX && !isCSV) { setError("Apenas arquivos .xlsx, .xls ou .csv são aceitos."); return; }

    if (isXLSX) {
      try {
        const XLSX = await import("xlsx");
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: "array", cellDates: true });
        const sheetName = wb.SheetNames.find((n: string) => !n.toLowerCase().includes("instru")) ?? wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        const rawRows: Record<string, string>[] = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
        const rows = rawRows.map((r: Record<string, string>) => {
          const normalized: Record<string, string> = {};
          for (const k of Object.keys(r)) normalized[k.toLowerCase().trim()] = String(r[k]).trim();
          return normalized;
        });
        if (rows.length === 0) { setError("Planilha vazia ou sem dados válidos."); return; }
        const items = rows.map(normalizeApptRow).filter(Boolean) as ParsedAppt[];
        if (items.length === 0) { setError("Nenhum agendamento válido encontrado. Verifique as colunas obrigatórias."); return; }
        setParsed(items);
      } catch { setError("Erro ao ler o arquivo XLSX. Verifique se não está corrompido."); }
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result as string;
        const lines = text.split(/\r?\n/).filter(Boolean);
        if (lines.length < 2) { setError("Arquivo CSV vazio."); return; }
        const headers = lines[0]!.split(";").map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
        const rows = lines.slice(1).map((line) => {
          const values = line.split(";").map((v) => v.trim().replace(/^"|"$/g, ""));
          const row: Record<string, string> = {};
          headers.forEach((h, i) => { row[h] = values[i] ?? ""; });
          return row;
        });
        const items = rows.map(normalizeApptRow).filter(Boolean) as ParsedAppt[];
        if (items.length === 0) { setError("Nenhum agendamento válido encontrado."); return; }
        setParsed(items);
      };
      reader.readAsText(file, "UTF-8");
    }
  };

  const handleImport = async () => {
    if (parsed.length === 0) return;
    const total = parsed.length;
    let totalInserted = 0;
    let totalSkipped = 0;
    const allErrors: string[] = [];
    setProgress({ done: 0, total });
    try {
      for (let i = 0; i < parsed.length; i += APPT_CHUNK_SIZE) {
        const chunk = parsed.slice(i, i + APPT_CHUNK_SIZE);
        const result = await importMutation.mutateAsync({ items: chunk });
        totalInserted += result.inserted;
        totalSkipped += result.skipped;
        allErrors.push(...(result.errors ?? []));
        setProgress({ done: Math.min(i + APPT_CHUNK_SIZE, total), total });
      }
      if (allErrors.length > 0) {
        toast.warning(`Importação concluída com avisos: ${totalInserted} inseridos, ${totalSkipped} ignorados. Veja o console para detalhes.`);
        console.warn("[Import] Erros:", allErrors.slice(0, 20));
      } else {
        toast.success(`Importação concluída: ${totalInserted} agendamentos inseridos, ${totalSkipped} ignorados.`);
      }
      utils.appointments.getByDateRange.invalidate();
      onSuccess();
      onClose();
      setParsed([]);
    } catch (e: any) {
      toast.error(e.message ?? "Erro ao importar agendamentos.");
    } finally {
      setProgress(null);
    }
  };

  const isImporting = progress !== null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setParsed([]); setError(""); } }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Importar Agendamentos
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
            <div>
              <p className="text-sm font-medium">Modelo de planilha</p>
              <p className="text-xs text-muted-foreground">Baixe o modelo com exemplos e instruções</p>
            </div>
            <Button variant="outline" size="sm" onClick={downloadAppointmentTemplate}>
              <FileText className="h-4 w-4 mr-2" />
              Baixar modelo (.xlsx)
            </Button>
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Selecionar arquivo (.xlsx ou .csv)</Label>
            <div
              className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Clique para selecionar ou arraste o arquivo</p>
              <p className="text-xs text-muted-foreground mt-1">Formatos: <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.csv</strong> — máximo 5.000 agendamentos</p>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFile} />
            </div>
          </div>
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
          {parsed.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <p className="text-sm font-medium text-green-700">{parsed.length} agendamento{parsed.length !== 1 ? "s" : ""} encontrado{parsed.length !== 1 ? "s" : ""} — prévia:</p>
              </div>
              <div className="overflow-x-auto border rounded-lg max-h-48">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Serviço</TableHead>
                      <TableHead>Profissional</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Hora</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsed.slice(0, 10).map((a, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm">{a.clientName}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{a.serviceName}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{a.professionalName}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{a.date}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{a.time}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{a.status ?? "completed"}</TableCell>
                      </TableRow>
                    ))}
                    {parsed.length > 10 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-2">
                          ... e mais {parsed.length - 10} agendamentos
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Os nomes de serviço e profissional devem ser exatamente iguais aos cadastrados no sistema.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); setParsed([]); setError(""); }}>Cancelar</Button>
          {isImporting && progress && (
            <div className="flex-1 mr-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>Importando...</span>
                <span>{progress.done} / {progress.total}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                />
              </div>
            </div>
          )}
          <Button onClick={handleImport} disabled={parsed.length === 0 || isImporting}>
            {isImporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {isImporting ? `${Math.round(((progress?.done ?? 0) / (progress?.total ?? 1)) * 100)}%` : `Importar ${parsed.length > 0 ? `${parsed.length} agendamentos` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
import AppointmentServicesModal from "@/components/AppointmentServicesModal";

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  completed: "bg-blue-100 text-blue-800",
  no_show: "bg-gray-100 text-gray-800",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmado",
  cancelled: "Cancelado",
  completed: "Concluído",
  no_show: "Não compareceu",
};

type QuickFilter = "today" | "tomorrow" | "week" | "month" | "custom";

const QUICK_FILTERS: { key: QuickFilter; label: string }[] = [
  { key: "today", label: "Hoje" },
  { key: "tomorrow", label: "Amanhã" },
  { key: "week", label: "Esta Semana" },
  { key: "month", label: "Este Mês" },
  { key: "custom", label: "Personalizado" },
];

function getDateRange(filter: QuickFilter, customStart: string, customEnd: string) {
  const now = new Date();
  switch (filter) {
    case "today":
      return { startDate: startOfDay(now), endDate: endOfDay(now) };
    case "tomorrow": {
      const tomorrow = addDays(now, 1);
      return { startDate: startOfDay(tomorrow), endDate: endOfDay(tomorrow) };
    }
    case "week":
      return { startDate: startOfWeek(now, { weekStartsOn: 1 }), endDate: endOfWeek(now, { weekStartsOn: 1 }) };
    case "month":
      return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
    case "custom": {
      const s = customStart ? parseISO(customStart) : startOfMonth(now);
      const e = customEnd ? parseISO(customEnd) : endOfMonth(now);
      return {
        startDate: isValid(s) ? startOfDay(s) : startOfMonth(now),
        endDate: isValid(e) ? endOfDay(e) : endOfMonth(now),
      };
    }
  }
}

// ─── Painel lateral de histórico do cliente ───────────────────────────────────
type ClientInfo = {
  name: string;
  phone: string | null;
  email: string | null;
};

function ClientHistorySheet({
  client,
  open,
  onClose,
  getServiceName,
  getProfName,
}: {
  client: ClientInfo | null;
  open: boolean;
  onClose: () => void;
  getServiceName: (id: number) => string;
  getProfName: (id: number) => string;
}) {
  const { data: history, isLoading } = trpc.clients.getHistory.useQuery(
    { clientPhone: client?.phone ?? "" },
    { enabled: open && !!client?.phone }
  );

  const totalGasto = useMemo(() => {
    if (!history) return 0;
    return history
      .filter((h) => h.status === "completed")
      .reduce((acc, h) => acc + Number(h.totalPrice ?? 0), 0);
  }, [history]);

  const totalAtendimentos = history?.filter((h) => h.status === "completed").length ?? 0;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            {client?.name ?? "Cliente"}
          </SheetTitle>
          {client?.phone && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Phone className="w-3.5 h-3.5" />
              {client.phone}
            </div>
          )}
        </SheetHeader>

        {/* Resumo */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-5">
          <Card className="border-border/40">
            <CardContent className="p-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Atendimentos</p>
                <p className="text-lg font-bold">{totalAtendimentos}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/40">
            <CardContent className="p-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-600 shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Total Gasto</p>
                <p className="text-lg font-bold">R$ {totalGasto.toFixed(2).replace(".", ",")}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Histórico */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Histórico de Atendimentos</h3>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : !history || history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
              Nenhum histórico encontrado.
            </div>
          ) : (
            history.map((h) => (
              <div key={h.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/30">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Calendar className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate">{getServiceName(h.serviceId)}</p>
                    <Badge className={`text-xs shrink-0 ${STATUS_COLORS[h.status]}`}>
                      {STATUS_LABELS[h.status]}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {getProfName(h.professionalId)} · {safeFormat(h.startTime, "dd/MM/yyyy HH:mm")}
                  </p>
                  {h.totalPrice && (
                    <p className="text-xs font-semibold text-primary mt-0.5">
                      R$ {Number(h.totalPrice).toFixed(2).replace(".", ",")}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function AdminAppointments() {
  const utils = trpc.useUtils();
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("today");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [customStart, setCustomStart] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [customEnd, setCustomEnd] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState<ClientInfo | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [cancelPinOpen, setCancelPinOpen] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<number | null>(null);
  const [editServicesAppt, setEditServicesAppt] = useState<any>(null);
  const [importOpen, setImportOpen] = useState(false);

  const cancelWithAuth = trpc.authorization.cancelAppointment.useMutation({
    onSuccess: () => {
      toast.success("Agendamento cancelado com autorização.");
      utils.appointments.getByDateRange.invalidate();
      setCancelPinOpen(false);
      setCancelTargetId(null);
    },
    onError: (e: { message: string }) => { throw new Error(e.message); },
  });

  const handleCancelClick = (id: number) => {
    setCancelTargetId(id);
    setCancelPinOpen(true);
  };

  const dateRange = useMemo(
    () => getDateRange(quickFilter, customStart, customEnd),
    [quickFilter, customStart, customEnd]
  );

  const { data: mySalon } = trpc.salon.getMySalon.useQuery();
  const salonId = mySalon?.id;

  const { data: appointments, isLoading } = trpc.appointments.getByDateRange.useQuery(dateRange);
  // Stable query inputs — objetos literais inline causam re-fetches infinitos
  const apptProfsInput = useMemo(() => ({ salonId: salonId! }), [salonId]);
  const apptServicesInput = useMemo(() => ({ salonId: salonId! }), [salonId]);
  const { data: professionals } = trpc.professionals.list.useQuery(apptProfsInput, { enabled: !!salonId });
  const { data: services } = trpc.services.list.useQuery(apptServicesInput, { enabled: !!salonId });

  const updateStatus = trpc.appointments.updateStatus.useMutation({
    onSuccess: () => { toast.success("Status atualizado!"); utils.appointments.getByDateRange.invalidate(); },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const getProfName = (id: number) => professionals?.find((p) => p.id === id)?.name ?? "—";
  const getServiceName = (id: number) => (services as Array<{ id: number; name: string }> | undefined)?.find((s) => s.id === id)?.name ?? "—";

  const filtered = useMemo(() => {
    let list = appointments ?? [];
    if (statusFilter !== "all") {
      list = list.filter((a: { status: string }) => a.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((a: { clientName?: string | null; clientPhone?: string | null; clientEmail?: string | null }) => {
        const name = (a.clientName ?? "").toLowerCase();
        const phone = (a.clientPhone ?? "").replace(/\D/g, "");
        const email = (a.clientEmail ?? "").toLowerCase();
        const qDigits = q.replace(/\D/g, "");
        return name.includes(q) || (qDigits && phone.includes(qDigits)) || email.includes(q);
      });
    }
    return list;
  }, [appointments, statusFilter, searchQuery]);

  // Navegar dia a dia quando filtro for "today" ou "tomorrow"
  const navigateDay = (direction: -1 | 1) => {
    if (quickFilter === "today" || quickFilter === "tomorrow") {
      const base = direction === 1 ? addDays(dateRange.startDate, 1) : addDays(dateRange.startDate, -1);
      const today = startOfDay(new Date());
      const isToday = base.getTime() === today.getTime();
      const isTomorrow = base.getTime() === addDays(today, 1).getTime();
      if (isToday) { setQuickFilter("today"); return; }
      if (isTomorrow) { setQuickFilter("tomorrow"); return; }
      setCustomStart(format(base, "yyyy-MM-dd"));
      setCustomEnd(format(base, "yyyy-MM-dd"));
      setQuickFilter("custom");
    }
  };

  const showDayNav = quickFilter === "today" || quickFilter === "tomorrow" || quickFilter === "custom";
  const currentDayLabel = safeFormat(dateRange.startDate, "EEEE, dd 'de' MMMM", "—");

  const openClientHistory = (appt: { clientName?: string | null; clientPhone?: string | null; clientEmail?: string | null }) => {
    if (!appt.clientPhone && !appt.clientName) return;
    setSelectedClient({
      name: appt.clientName ?? "Cliente",
      phone: appt.clientPhone ?? null,
      email: appt.clientEmail ?? null,
    });
    setHistoryOpen(true);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-[Cormorant_Garamond,serif] font-semibold">Agendamentos</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Gerencie todos os agendamentos</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Importar Agendamentos
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col gap-3">
        {/* Filtros rápidos de período */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-border/50 overflow-hidden">
            {QUICK_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setQuickFilter(f.key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                  quickFilter === f.key
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-secondary text-muted-foreground"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Filtro de status */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-8 text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Campo de busca */}
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            placeholder="Buscar por nome ou telefone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 text-xs pl-8 pr-8"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Datas personalizadas */}
        {quickFilter === "custom" && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">De:</Label>
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="h-8 text-xs w-36"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">Até:</Label>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="h-8 text-xs w-36"
              />
            </div>
          </div>
        )}

        {/* Navegação dia a dia */}
        {showDayNav && quickFilter !== "custom" && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => navigateDay(-1)}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="text-sm font-medium text-foreground capitalize min-w-[200px] text-center">
              {currentDayLabel}
            </span>
            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => navigateDay(1)}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Contagem */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {isLoading ? "Carregando..." : `${filtered.length} agendamento${filtered.length !== 1 ? "s" : ""} encontrado${filtered.length !== 1 ? "s" : ""}`}
        </span>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Nenhum agendamento encontrado para este período.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((appt) => (
            <Card key={appt.id} className="border-border/50 hover:border-primary/30 transition-all">
              <CardContent className="p-3 md:p-4">
                <div className="flex flex-col gap-3">
                  {/* Linha 1: info principal + status */}
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Calendar className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-foreground text-sm leading-snug truncate">{getServiceName(appt.serviceId)}</h3>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-sm font-semibold text-primary whitespace-nowrap">
                            R$ {Number(appt.totalPrice ?? 0).toFixed(2).replace(".", ",")}
                          </span>
                          <Badge className={`text-xs ${STATUS_COLORS[appt.status]}`}>
                            {STATUS_LABELS[appt.status]}
                          </Badge>
                        </div>
                      </div>
                      {appt.clientName && (
                        <button
                          onClick={() => openClientHistory(appt)}
                          className="text-xs text-primary font-medium mt-0.5 hover:underline flex items-center gap-1 group"
                          title="Ver histórico do cliente"
                        >
                          {appt.clientName}
                          <History className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      )}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <User className="w-3 h-3" /> {getProfName(appt.professionalId)}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" /> {safeFormat(appt.startTime, "dd/MM/yyyy")}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" /> {safeFormat(appt.startTime, "HH:mm")}–{safeFormat(appt.endTime, "HH:mm")}
                        </div>
                        {appt.clientPhone && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Phone className="w-3 h-3" /> {appt.clientPhone}
                          </div>
                        )}
                      </div>
                      {appt.notes && <p className="text-xs text-muted-foreground mt-1 italic">"{appt.notes}"</p>}
                    </div>
                  </div>
                  {/* Linha 2: botões de ação */}
                  {appt.status !== "cancelled" && appt.status !== "completed" && (
                    <div className="flex flex-wrap gap-1.5 pl-12">
                      {appt.status === "pending" && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => updateStatus.mutate({ id: appt.id, status: "confirmed" })}
                          disabled={updateStatus.isPending}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" /> Confirmar
                        </Button>
                      )}
                      {appt.status === "confirmed" && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => updateStatus.mutate({ id: appt.id, status: "completed" })}
                          disabled={updateStatus.isPending}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" /> Concluir
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => setEditServicesAppt(appt)}
                      >
                        <Scissors className="w-3 h-3 mr-1" /> Serviços
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-destructive border-destructive/30"
                        onClick={() => handleCancelClick(appt.id)}
                        disabled={updateStatus.isPending}
                      >
                        <ShieldAlert className="w-3 h-3 mr-1" /> Cancelar
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Painel lateral de histórico do cliente */}
      <ClientHistorySheet
        client={selectedClient}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        getServiceName={getServiceName}
        getProfName={getProfName}
      />

      {/* Modal de edição de serviços */}
      <AppointmentServicesModal
        open={!!editServicesAppt}
        onClose={() => setEditServicesAppt(null)}
        appointment={editServicesAppt}
        salonId={salonId ?? 0}
        onSaved={() => utils.appointments.getByDateRange.invalidate()}
      />

      {/* Modal de PIN para cancelamento */}
      <AuthPinModal
        open={cancelPinOpen}
        onClose={() => { setCancelPinOpen(false); setCancelTargetId(null); }}
        title="Cancelar Agendamento"
        description="Esta ação requer autorização. Digite o PIN de administrador para cancelar este agendamento."
        onConfirm={async (pin) => {
          if (!cancelTargetId) return;
          await cancelWithAuth.mutateAsync({ appointmentId: cancelTargetId, pin });
        }}
      />

      {/* Modal de importação em massa */}
      <ImportAppointmentsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => utils.appointments.getByDateRange.invalidate()}
      />
    </div>
  );
}
